// src/Component/Workshop/Notifications/NotificationPanel.jsx
// Slide-out notification panel — triggered by the mail icon in the navbar.
// Shows all auto + manual email notifications from notification_logs.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotifications, useMarkNotificationRead } from "../../../hooks/useWorkshopQueries";
import { Spinner } from "../UI/index";

// ── Notification type config ─────────────────────────────────────────────────
const TYPE_META = {
  AUTO:   { label: "Auto",   color: "#f97316", bg: "rgba(249,115,22,.1)" },
  MANUAL: { label: "Manual", color: "#3b82f6", bg: "rgba(59,130,246,.1)" },
  EVENT:  { label: "Event",  color: "#22c55e", bg: "rgba(34,197,94,.1)"  },
};

const STATUS_ICON = {
  SENT:   { icon: "✓", color: "#22c55e" },
  FAILED: { icon: "✕", color: "#ef4444" },
};

function ruleLabel(ruleCode) {
  if (!ruleCode) return "Event Email";
  return ruleCode
    .replace(/_/g, " ")
    .replace(/DELAY$/i, "Delay")
    .replace(/REMINDER$/i, "Reminder")
    .split(" ")
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function relativeTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const TABS = [
  { key: "ALL",    label: "All" },
  { key: "AUTO",   label: "Auto" },
  { key: "MANUAL", label: "Manual" },
  { key: "EVENT",  label: "Event" },
];

export default function NotificationPanel({ open, onClose }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("ALL");
  const { data, isLoading } = useNotifications({});
  const markRead = useMarkNotificationRead();

  if (!open) return null;

  const notifications = data?.notifications || [];
  const unreadCount = data?.unreadCount || 0;

  const filtered = activeTab === "ALL"
    ? notifications
    : notifications.filter(n => n.notificationType === activeTab);

  const handleMarkAllRead = () => {
    markRead.mutate({ markAll: true });
  };

  const handleNotificationClick = (n) => {
    if (!n.isRead) {
      markRead.mutate({ notificationId: n.id });
    }
    if (n.caseId) {
      navigate(`/admin/cases/${n.caseId}`, { state: { activeTab: "Timeline" } });
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 998,
          background: "rgba(0,0,0,.25)", backdropFilter: "blur(2px)",
        }}
      />

      {/* Panel */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: 420, maxWidth: "100vw", zIndex: 999,
        background: "var(--wd-card)", borderLeft: "1px solid var(--wd-border)",
        display: "flex", flexDirection: "column",
        boxShadow: "-8px 0 30px rgba(0,0,0,.25)",
        animation: "slideInRight .2s ease-out",
      }}>
        {/* Header */}
        <div style={{
          padding: "16px 20px", borderBottom: "1px solid var(--wd-border)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: "var(--wd-t1)" }}>
              Notifications
            </span>
            {unreadCount > 0 && (
              <span style={{
                fontSize: 10, fontWeight: 700, color: "#fff",
                background: "#ef4444", borderRadius: 10, padding: "1px 7px",
                minWidth: 18, textAlign: "center",
              }}>
                {unreadCount}
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: 11, fontWeight: 600, color: "var(--wd-blue)",
                  padding: "4px 8px",
                }}
              >
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: "var(--wd-t3)", padding: 4, display: "flex",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Filter tabs */}
        <div style={{
          display: "flex", gap: 4, padding: "10px 20px 0",
          borderBottom: "1px solid var(--wd-border)",
        }}>
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: 12, fontWeight: 600, padding: "6px 12px",
                color: activeTab === t.key ? "var(--wd-blue)" : "var(--wd-t3)",
                borderBottom: activeTab === t.key ? "2px solid var(--wd-blue)" : "2px solid transparent",
                marginBottom: -1,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Notification list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
          {isLoading ? (
            <Spinner />
          ) : filtered.length === 0 ? (
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", height: 200, color: "var(--wd-t3)",
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ opacity: 0.4, marginBottom: 12 }}>
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              <span style={{ fontSize: 13, fontWeight: 500 }}>No notifications yet</span>
            </div>
          ) : (
            filtered.map(n => {
              const typeMeta = TYPE_META[n.notificationType] || TYPE_META.AUTO;
              const statusMeta = STATUS_ICON[n.emailStatus] || STATUS_ICON.SENT;
              return (
                <div
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  style={{
                    display: "flex", gap: 12, padding: "12px 20px",
                    cursor: "pointer", transition: "background .12s",
                    borderBottom: "1px solid var(--wd-border)",
                    background: n.isRead ? "transparent" : "rgba(59,130,246,.04)",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--wd-hover)"}
                  onMouseLeave={e => e.currentTarget.style.background = n.isRead ? "transparent" : "rgba(59,130,246,.04)"}
                >
                  {/* Unread dot */}
                  <div style={{ paddingTop: 6, width: 8, flexShrink: 0 }}>
                    {!n.isRead && (
                      <div style={{
                        width: 7, height: 7, borderRadius: "50%",
                        background: "var(--wd-blue)",
                      }} />
                    )}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3, flexWrap: "wrap" }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 6,
                        background: typeMeta.bg, color: typeMeta.color,
                      }}>
                        {typeMeta.label}
                      </span>
                      {n.caseId && (
                        <span style={{
                          fontFamily: "var(--wd-mono)", fontSize: 11, fontWeight: 700,
                          color: "var(--wd-blue)",
                        }}>
                          {n.caseId}
                        </span>
                      )}
                      <span style={{ fontSize: 10, color: statusMeta.color, fontWeight: 700 }}>
                        {statusMeta.icon} {n.emailStatus}
                      </span>
                    </div>

                    <div style={{
                      fontSize: 12, fontWeight: 600, color: "var(--wd-t1)",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {n.subject || ruleLabel(n.ruleCode)}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3 }}>
                      <span style={{ fontSize: 11, color: "var(--wd-t3)" }}>
                        To: {n.recipientEmail || n.recipientRole || "—"}
                      </span>
                      <span style={{ fontSize: 10, color: "var(--wd-t3)" }}>
                        · {relativeTime(n.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Slide-in animation */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </>
  );
}
